import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

/**
 * Date ranges, and the presets that produce them.
 *
 * A range is two calendar days as `YYYY-MM-DD`, both inclusive. Plain strings
 * rather than Date objects: they survive JSON, URLs and state untouched, and
 * never pick up a timezone shift on the way.
 */
export const ISO_DAY = 'YYYY-MM-DD';

export interface DateRange {
  start: string;
  end: string;
}

/** 0 = weeks start on Sunday, 1 = Monday. */
export type WeekStart = 0 | 1;

export interface DateRangePreset {
  id: string;
  label: string;
  /** Optional second line under the label, e.g. what the choice means. */
  description?: string;
  /** The range, counted back from `today`. */
  resolve: (today: Dayjs, weekStartsOn: WeekStart) => { start: Dayjs; end: Dayjs };
}

export function startOfWeek(day: Dayjs, weekStartsOn: WeekStart): Dayjs {
  const offset = (day.day() - weekStartsOn + 7) % 7;
  return day.subtract(offset, 'day').startOf('day');
}

/**
 * The last `count` complete calendar months, ending with last month.
 * The month in progress is left out, so every month shown is whole.
 */
export function lastFullMonths(
  count: number,
  overrides: Partial<Pick<DateRangePreset, 'id' | 'label' | 'description'>> = {},
): DateRangePreset {
  return {
    id: overrides.id ?? `last_${count}_full_months`,
    label: overrides.label ?? `Last ${count} months`,
    description: overrides.description,
    resolve: (today) => {
      const thisMonth = today.startOf('month');
      return { start: thisMonth.subtract(count, 'month'), end: thisMonth.subtract(1, 'day') };
    },
  };
}

/** The last `count` days, today included. */
export function lastDays(count: number): DateRangePreset {
  return {
    id: `last_${count}_days`,
    label: `Last ${count} days`,
    resolve: (today) => ({ start: today.subtract(count - 1, 'day'), end: today }),
  };
}

/** Every built-in preset. Pages pick the ones they need. */
export const DATE_PRESETS = {
  today: {
    id: 'today',
    label: 'Today',
    resolve: (today) => ({ start: today, end: today }),
  },
  yesterday: {
    id: 'yesterday',
    label: 'Yesterday',
    resolve: (today) => ({ start: today.subtract(1, 'day'), end: today.subtract(1, 'day') }),
  },
  last7Days: lastDays(7),
  last30Days: lastDays(30),
  last90Days: lastDays(90),
  thisWeek: {
    id: 'this_week',
    label: 'This week',
    resolve: (today, weekStartsOn) => ({ start: startOfWeek(today, weekStartsOn), end: today }),
  },
  lastWeek: {
    id: 'last_week',
    label: 'Last week',
    resolve: (today, weekStartsOn) => {
      const start = startOfWeek(today, weekStartsOn).subtract(1, 'week');
      return { start, end: start.add(6, 'day') };
    },
  },
  thisMonth: {
    id: 'this_month',
    label: 'This month',
    resolve: (today) => ({ start: today.startOf('month'), end: today }),
  },
  lastMonth: {
    id: 'last_month',
    label: 'Last month',
    resolve: (today) => {
      const month = today.subtract(1, 'month');
      return { start: month.startOf('month'), end: month.endOf('month') };
    },
  },
  thisQuarter: {
    id: 'this_quarter',
    label: 'This quarter',
    resolve: (today) => ({ start: today.startOf('quarter'), end: today }),
  },
  lastQuarter: {
    id: 'last_quarter',
    label: 'Last quarter',
    resolve: (today) => {
      const quarter = today.subtract(1, 'quarter');
      return { start: quarter.startOf('quarter'), end: quarter.endOf('quarter') };
    },
  },
  yearToDate: {
    id: 'year_to_date',
    label: 'Year to date',
    resolve: (today) => ({ start: today.startOf('year'), end: today }),
  },
  lastYear: {
    id: 'last_year',
    label: 'Last year',
    resolve: (today) => {
      const year = today.subtract(1, 'year');
      return { start: year.startOf('year'), end: year.endOf('year') };
    },
  },
  last12Months: lastFullMonths(12),
  // Twelve monthly cohorts up to today: the eleven months before this one,
  // then this month so far. What the dashboards open on.
  last12MonthsToDate: {
    id: 'last_12_months_to_date',
    label: 'Last 12 months',
    resolve: (today) => ({ start: today.startOf('month').subtract(11, 'month'), end: today }),
  },
} satisfies Record<string, DateRangePreset>;

/** What a picker offers when the page does not choose its own presets. */
export const DEFAULT_DATE_PRESETS: DateRangePreset[] = [
  DATE_PRESETS.last7Days,
  DATE_PRESETS.last30Days,
  DATE_PRESETS.last90Days,
  DATE_PRESETS.thisMonth,
  DATE_PRESETS.lastMonth,
  DATE_PRESETS.thisQuarter,
  DATE_PRESETS.lastQuarter,
  DATE_PRESETS.yearToDate,
  DATE_PRESETS.lastYear,
  DATE_PRESETS.last12Months,
];

export function toRange(start: Dayjs, end: Dayjs): DateRange {
  return { start: start.format(ISO_DAY), end: end.format(ISO_DAY) };
}

export function resolvePreset(
  preset: DateRangePreset,
  today: Dayjs = dayjs(),
  weekStartsOn: WeekStart = 0,
): DateRange {
  const { start, end } = preset.resolve(today.startOf('day'), weekStartsOn);
  return toRange(start, end);
}

/** The preset that produces exactly this range today, if any. */
export function findPreset(
  range: DateRange | null,
  presets: DateRangePreset[],
  today: Dayjs = dayjs(),
  weekStartsOn: WeekStart = 0,
): DateRangePreset | null {
  if (!range) return null;
  return (
    presets.find((p) => {
      const r = resolvePreset(p, today, weekStartsOn);
      return r.start === range.start && r.end === range.end;
    }) ?? null
  );
}

/** True when the range runs from the first of a month to the last of one. */
export function isWholeMonths(range: DateRange): boolean {
  const start = dayjs(range.start);
  const end = dayjs(range.end);
  return start.date() === 1 && end.isSame(end.endOf('month'), 'day');
}

/**
 * The range in words.
 *
 * `day` — "4 Aug – 31 Aug 2026", with the year on both ends when they differ.
 * `month` — "Aug 2025 – Aug 2026", for ranges made of whole months; anything
 * else falls back to days, because naming only months would hide the cut.
 */
export function formatDateRange(range: DateRange, style: 'day' | 'month' = 'day'): string {
  const start = dayjs(range.start);
  const end = dayjs(range.end);
  const sameYear = start.year() === end.year();

  if (style === 'month' && isWholeMonths(range)) {
    if (start.isSame(end, 'month')) return end.format('MMM YYYY');
    return `${start.format(sameYear ? 'MMM' : 'MMM YYYY')} – ${end.format('MMM YYYY')}`;
  }
  if (start.isSame(end, 'day')) return end.format('D MMM YYYY');
  return `${start.format(sameYear ? 'D MMM' : 'D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
}

/** How long the range is, in the unit a person would use: "13 months", "12 days". */
export function describeSpan(range: DateRange): string {
  const start = dayjs(range.start);
  const end = dayjs(range.end);
  if (isWholeMonths(range)) {
    const months = end.diff(start, 'month') + 1;
    return months === 1 ? '1 month' : `${months} months`;
  }
  const days = end.diff(start, 'day') + 1;
  return days === 1 ? '1 day' : `${days} days`;
}
